import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import RatingStars from "./RatingStars";
// ─── Data ─────────────────────────────────────────────────────────────────────

const SATISFACTION_LEVELS = [
  { value: 1, emoji: "😞", label: "מאוד לא מרוצה", selected: "bg-red-500    border-red-500"    },
  { value: 2, emoji: "😕", label: "לא מרוצה",       selected: "bg-orange-400 border-orange-400" },
  { value: 3, emoji: "😐", label: "בסדר",            selected: "bg-yellow-400 border-yellow-400" },
  { value: 4, emoji: "😊", label: "מרוצה",           selected: "bg-emerald-500 border-emerald-500"},
  { value: 5, emoji: "😄", label: "מאוד מרוצה",     selected: "bg-indigo-500 border-indigo-500" },
];

const DISPLAY_MAP = {
  1: { emoji: "😞", label: "מאוד לא מרוצה", bg: "bg-red-50",     text: "text-red-700",     ring: "ring-red-200"     },
  2: { emoji: "😕", label: "לא מרוצה",       bg: "bg-orange-50",  text: "text-orange-700",  ring: "ring-orange-200"  },
  3: { emoji: "😐", label: "בסדר",            bg: "bg-yellow-50",  text: "text-yellow-700",  ring: "ring-yellow-200"  },
  4: { emoji: "😊", label: "מרוצה",           bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" },
  5: { emoji: "😄", label: "מאוד מרוצה",     bg: "bg-indigo-50",  text: "text-indigo-700",  ring: "ring-indigo-200"  },
};

// ─── Modal ────────────────────────────────────────────────────────────────────

const SatisfactionModal = ({ isOpen, onClose, onSubmit, lessonTitle, teacherName }) => {
  const [selectedRating, setSelectedRating] = useState(null);
  const [isSubmitting, setIsSubmitting]     = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedRating(null);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape" && !isSubmitting) onClose?.(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, isSubmitting, onClose]);

  const handleSubmit = async () => {
    if (!selectedRating || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(selectedRating);
      onClose?.();
    } catch (err) {
      console.error("Rating submission failed:", err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm"
        onClick={() => { if (!isSubmitting) onClose?.(); }}
      />

      {/* Sheet — slides up on mobile, centered on desktop */}
      <div
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-label="דירוג שיעור"
        className="fixed inset-x-0 bottom-0 z-[9999] flex flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:inset-0 sm:m-auto sm:h-fit sm:max-w-md sm:rounded-3xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-sm">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                  fill="white" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800">איך היה השיעור?</h2>
              {(lessonTitle || teacherName) && (
                <p className="text-xs text-slate-500">
                  {lessonTitle && <span className="font-medium">{lessonTitle}</span>}
                  {teacherName && <span className="text-indigo-500"> · {teacherName}</span>}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => { if (!isSubmitting) onClose?.(); }}
            aria-label="סגור"
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-white hover:text-slate-600"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <p className="mb-4 text-center text-sm text-slate-500">בחר את רמת שביעות הרצון שלך מהשיעור</p>

          <div className="grid grid-cols-5 gap-2">
            {SATISFACTION_LEVELS.map((level) => {
              const isSelected = selectedRating === level.value;
              return (
                <button
                  key={level.value}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setSelectedRating(level.value)}
                  className={`relative flex flex-col items-center gap-1.5 rounded-2xl border-2 py-3 transition-all duration-200
                    ${isSelected
                      ? `${level.selected} scale-105 shadow-md`
                      : "border-gray-200 bg-gray-50 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-white hover:shadow-sm"
                    }
                    ${isSubmitting ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
                  `}
                >
                  <span className="text-2xl">{level.emoji}</span>
                  <span className={`text-center text-[9px] font-semibold leading-tight sm:text-[10px] ${isSelected ? "text-white" : "text-slate-500"}`}>
                    {level.label}
                  </span>
                  {isSelected && (
                    <div className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-white shadow ring-1 ring-gray-100">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="#6366f1">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-white/80 px-5 py-4 backdrop-blur-sm">
          <button
            type="button"
            onClick={() => { if (!isSubmitting) onClose?.(); }}
            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-slate-500 transition hover:bg-gray-50"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedRating || isSubmitting}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white transition-all
              ${selectedRating && !isSubmitting
                ? "bg-gradient-to-r from-indigo-500 to-violet-600 shadow-sm hover:-translate-y-px hover:shadow-md"
                : "cursor-not-allowed bg-gray-200 text-gray-400"
              }`}
          >
            {isSubmitting ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <>
                {selectedRating && <span>{SATISFACTION_LEVELS[selectedRating - 1].emoji}</span>}
                <span>שלח דירוג</span>
              </>
            )}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SatisfactionRatingSystem = ({ lesson, onRate, currentRating = 0, canRate = false }) => {
  const [rating, setRating]           = useState(Number(currentRating));
  const [showModal, setShowModal]     = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => { setRating(Number(currentRating)); }, [currentRating]);

  const handleRatingSubmit = async (selectedRating) => {
    try {
      await onRate(Number(selectedRating));
      setRating(Number(selectedRating));
      setShowModal(false);
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 800);
    } catch (err) {
      console.error("Failed to submit rating:", err);
      throw err;
    }
  };

  // ── Rated state ──
  if (rating > 0) {
    const d = DISPLAY_MAP[Number(rating)];
    if (!d) return null;

    return (
      <div>
        <RatingStars value={rating} size={15} />
      </div>
    );
  }

  // ── Waiting state ──
  return (
    <>
    <button
      type="button"
      dir="rtl"
      onClick={() => { if (canRate) setShowModal(true); }}
      disabled={!canRate}
      className={`inline-flex items-center justify-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold text-center transition-all
        ${canRate
          ? "cursor-pointer border-indigo-200 bg-indigo-50 text-indigo-600 hover:-translate-y-px hover:bg-indigo-100 hover:shadow-sm"
          : "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
        }`}
    >
      <span className="inline-flex items-center">
        ⏳
      </span>
      <span className="inline-flex items-center">ממתין לדירוג</span>
      {canRate && (
        <span className="inline-flex items-center text-yellow-500">
          ⭐
        </span>
      )}
    </button>

      <SatisfactionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleRatingSubmit}
        lessonTitle={lesson?.title}
        teacherName={lesson?.teacher?.name}
      />
    </>
  );
};

export default SatisfactionRatingSystem;